import {
  Box,
  DrawerCloseButton,
  Button as CButton,
  DrawerContent,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import React, { useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import { IconBan, IconPen, IconTrashbin, IconThunder } from 'assets/icons';
import Button from 'components/Button';
import Drawer, { ControllableDrawer } from 'components/Drawer';
import { ConfirmationModal } from 'components/Modal';
import SearchField from 'components/SearchField';
import Table, { IndeterminateCheckbox } from 'components/Table';
import Title from 'components/Title';
import OvertimeAlertForm from 'containers/CompanySettings/RightDrawer/OvertimeAlertForm';
import { initialOvertimeAlertData } from 'containers/CompanySettings/RightDrawer/OvertimeAlertForm/schema';
import { hourOptions } from 'containers/CompanySettings/shared/options';
import { useActivateOvertimeAlerts } from 'hooks/companySettings/useActivatateOvertimeAlerts';
import { useDeactivateOvertimeAlerts } from 'hooks/companySettings/useDeactivateOvertimeAlerts';
import { IDeleteOvertimeAlerts, useDeleteOvertimeAlerts } from 'hooks/companySettings/useDeleteOvertimeAlerts';
import { IOvertimeAlertSearchQuery, useFetchOvertimeAlerts } from 'hooks/companySettings/useFetchOvertimeAlerts';
import { secondsToHm } from 'utils/helpers';

import TableFooter from './TableFooter';

const initialSearchQuery = {
  page: 1,
  page_size: 10,
  search: '',
};

const OvertimeAlertList: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'companySettings']);

  const [searchQuery, setSearchQuery] = useState<any>(initialSearchQuery);
  const [formData, setFormData] = useState<typeof initialOvertimeAlertData>(initialOvertimeAlertData);
  const [dialogIds, setDialogIds] = useState<IDeleteOvertimeAlerts>({ ids: [] });

  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();
  const {
    isOpen: isDeactivateModalOpen,
    onOpen: onDeactivateModalOpen,
    onClose: onDeactivateModalClose,
  } = useDisclosure();
  const { isOpen: isActivateModalOpen, onOpen: onActivateModalOpen, onClose: onActivateModalClose } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

  const { status: activateOvertimeAlertStatus, mutateAsync: mutateAsyncActivate } = useActivateOvertimeAlerts();
  const { status: deactivateOvertimeAlertStatus, mutateAsync: mutateAsyncDeactivate } = useDeactivateOvertimeAlerts();
  const { status: deleteOvertimeAlertStatus, mutateAsync: mutateAsyncDelete } = useDeleteOvertimeAlerts();
  const { data, status } = useFetchOvertimeAlerts(searchQuery);

  const refetch = () => null;

  const columns = useMemo(
    () => [
      {
        id: 'selection',
        width: 30,
        Header: ({ getToggleAllRowsSelectedProps }: any) => {
          return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} colorScheme="secondary" />;
        },
        Cell: ({ row }: any) => {
          return <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} colorScheme="secondary" />;
        },
      },
      {
        Header: 'Alert Name',
        accessor: 'name',
      },
      {
        Header: 'Alert time',
        accessor: 'is_before',
        Cell: (data: CellValue) => {
          const { hours, minutes } = secondsToHm(data.row.original.before_after_overtime);

          return (
            <Text>
              {hours}:{minutes}:00 <br />
              {data.row.original?.is_before ? 'Before Overtime' : 'After Overtime'}
            </Text>
          );
        },
      },
      {
        Header: 'Overtime Type',
        accessor: 'overtime_type',
      },
      {
        Header: 'Status',
        accessor: 'active',
        Cell: (data: CellValue) => {
          return <Text>{data.row.original?.is_active ? t('common:active') : t('common:inactive')} </Text>;
        },
      },
      {
        Header: 'Assigned Users',
        accessor: 'assigned_users',
        width: 220,
        Cell: (data: CellValue) => {
          return (
            <Box>
              <Menu>
                <MenuButton as={CButton} variant="unstyled">
                  <Text fontWeight={700} color="primary.500">
                    {Array.isArray(data.value) && data.value.length
                      ? data.value.length === 1
                        ? `${data.value[0]?.name} `
                        : `${data.value[0]?.name} and ${data.value.length - 1} others`
                      : '-----'}
                  </Text>
                </MenuButton>
                {data.value.length > 0 ? (
                  <MenuList>
                    {data.value.map((item: any, index: number) => (
                      <MenuItem color="primary.500" fontWeight="600" key={index}>
                        {item?.name}
                      </MenuItem>
                    ))}
                  </MenuList>
                ) : null}
              </Menu>
            </Box>
          );
        },
      },
      {
        Header: '',
        accessor: 'action',
        Cell: (data: CellValue) => {
          return (
            <Box>
              <Tooltip label="Update alert" hasArrow bg="black">
                <IconButton
                  aria-label="Update alert"
                  bg="rgba(38, 103, 201, 0.1)"
                  icon={<Icon as={IconPen} />}
                  size="sm"
                  mr="3"
                  _hover={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                  onClick={() => handleEditIconClick(data.row.original)}
                />
              </Tooltip>
              {data.row.original?.is_active ? (
                <Tooltip label="Deactivate alert" hasArrow bg="black">
                  <IconButton
                    aria-label="Deactivate alert"
                    bg="rgba(101, 104, 134, 0.1)"
                    icon={<Icon as={IconBan} />}
                    size="sm"
                    mr="3"
                    _hover={{ bg: 'rgba(101, 104, 134, 0.2)' }}
                    onClick={() => handleDeactivateIconClick(data.row.original?.id)}
                  />
                </Tooltip>
              ) : (
                <Tooltip label="Activate alert" hasArrow bg="black">
                  <IconButton
                    aria-label="Activate alert"
                    bg="rgba(101, 104, 134, 0.1)"
                    icon={<Icon as={IconThunder} />}
                    size="sm"
                    mr="3"
                    _hover={{ bg: 'rgba(101, 104, 134, 0.2)' }}
                    onClick={() => handleActivateIconClick(data.row.original?.id)}
                  />
                </Tooltip>
              )}
              <Tooltip label="Delete alert" hasArrow bg="black">
                <IconButton
                  aria-label="Delete alert"
                  bg="rgba(255, 83, 83, 0.1)"
                  icon={<Icon as={IconTrashbin} />}
                  size="sm"
                  _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                  onClick={() => handleDeleteIconClick(data.row.original?.id)}
                />
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handleEditIconClick = (overtimeAlertData: any) => {
    const { hours } = secondsToHm(overtimeAlertData.before_after_overtime);

    const formData: typeof initialOvertimeAlertData = {
      id: overtimeAlertData.id,
      name: overtimeAlertData.name,
      overtime_type: overtimeAlertData.overtime_type,
      is_before: overtimeAlertData.is_before ? 'before' : 'after',
      alert_time_hour: hours ? hourOptions.find(hour => hour.value === Number(hours)) : null,
      send_notification_by_mail: overtimeAlertData.send_notification_by_mail ? true : false,
      send_notification_by_push: overtimeAlertData.send_notification_by_push ? true : false,
      user_ids: Array.isArray(overtimeAlertData?.assigned_users)
        ? overtimeAlertData?.assigned_users.map((user: any) => ({
            userId: user.id,
            name: user.name,
            firstName: user.name.split(',')[0] ?? '',
            lastName: user.name.split(',')[1] ?? '',
          }))
        : [],
      group_ids: [],
    };

    setFormData(formData);
    onDrawerOpen();
  };

  const handleDrawerClose = () => {
    setFormData(initialOvertimeAlertData);
    onDrawerClose();
  };

  const handleDeactivateIconClick = (id: number) => {
    setDialogIds({ ids: [id] });
    onDeactivateModalOpen();
  };

  const handleActivateIconClick = (id: number) => {
    setDialogIds({ ids: [id] });
    onActivateModalOpen();
  };

  const handleDeleteIconClick = (id: number) => {
    setDialogIds({ ids: [id] });
    onDeleteModalOpen();
  };

  const deactivateOvertimeAlert = () => {
    mutateAsyncDeactivate(dialogIds).then(() => {
      refetch();
      onDeactivateModalClose();
      setDialogIds({ ids: [] });
    });
  };

  const activateOvertimeAlert = () => {
    mutateAsyncActivate(dialogIds).then(() => {
      refetch();
      onActivateModalClose();
      setDialogIds({ ids: [] });
    });
  };

  const deleteOvertimeAlert = () => {
    mutateAsyncDelete(dialogIds).then(() => {
      refetch();
      onDeleteModalClose();
      setDialogIds({ ids: [] });
    });
  };

  const handleBulkActivate = (selectedRows: any) => {
    const ids = selectedRows.map((row: any) => row.original.id);
    setDialogIds({ ids: ids });
    onActivateModalOpen();
  };

  const handleBulkDeactivate = (selectedRows: any) => {
    const ids = selectedRows.map((row: any) => row.original.id);
    setDialogIds({ ids: ids });
    onDeactivateModalOpen();
  };

  const handleBulkDelete = (selectedRows: any) => {
    const ids = selectedRows.map((row: any) => row.original.id);
    setDialogIds({ ids: ids });
    onDeleteModalOpen();
  };

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IOvertimeAlertSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  return (
    <>
      <ControllableDrawer onClose={handleDrawerClose} isOpen={isDrawerOpen}>
        <DrawerContent>
          <DrawerCloseButton />
          <OvertimeAlertForm onClose={handleDrawerClose} formData={formData} />
        </DrawerContent>
      </ControllableDrawer>
      <ConfirmationModal
        isOpen={isDeactivateModalOpen}
        onClose={onDeactivateModalClose}
        title={t('companySettings:deactivateOvertimeAlertModalTitle')}
        subTitle=""
        confirmButtonLabel={t('common:deactivate')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: deactivateOvertimeAlertStatus === 'loading',
          onClick: deactivateOvertimeAlert,
        }}
      />
      <ConfirmationModal
        isOpen={isActivateModalOpen}
        onClose={onActivateModalClose}
        title={t('companySettings:activateOvertimeAlertModalTitle')}
        subTitle=""
        confirmButtonLabel={t('common:activate')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: activateOvertimeAlertStatus === 'loading',
          onClick: activateOvertimeAlert,
        }}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        title={t('companySettings:deleteOvertimeAlertTitle')}
        subTitle={t('companySettings:deleteOvertimeAlertSubtitle')}
        confirmButtonLabel={t('common:delete')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: deleteOvertimeAlertStatus === 'loading',
          onClick: deleteOvertimeAlert,
        }}
      />
      <Flex mb="4" justifyContent="space-between">
        <Title hideBackButton={true}>Overtime Alerts</Title>
        <Drawer
          drawerTrigger={onOpen => (
            <Button colorScheme="secondary" mt={{ base: '2', md: '5' }} onClick={onOpen}>
              + Add overtime alerts
            </Button>
          )}
        >
          {onClose => <OvertimeAlertForm onClose={onClose} />}
        </Drawer>
      </Flex>
      <Box ml="auto" maxWidth="250px" width="100%">
        <SearchField
          inputProps={{
            placeholder: 'Search alert',
            onChange: handleSearchInputChange,
          }}
        />
      </Box>
      <Table
        columns={columns}
        data={data?.payload || []}
        enableRowSelect
        paginationData={data?.meta_data?.pagination}
        onPageChange={handlePageChange}
        isLoading={status === 'loading'}
        emptyTableStateProps={{
          searchText: searchQuery.search,
        }}
        renderTableFooter={(selectedFlatRows: any, toggleAllRowSelected: any) => {
          return (
            <TableFooter
              selectedFlatRows={selectedFlatRows}
              toggleAllRowSelected={toggleAllRowSelected}
              handleBulkDeactivate={handleBulkDeactivate}
              handleBulkDelete={handleBulkDelete}
              handleBulkActivate={handleBulkActivate}
            />
          );
        }}
      />
    </>
  );
};
export default OvertimeAlertList;
